import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '../App';
import { useEvent } from '../contexts/EventContext';
import Logo from '../components/Logo';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { LogOut, Car, Users, Calendar, Settings, ChevronDown } from 'lucide-react';
import { toast } from 'sonner';

// Tab Components
import CarsTab from '../components/admin/CarsTab';
import UsersTab from '../components/admin/UsersTab';
import EventsTab from '../components/admin/EventsTab';
import SettingsTab from '../components/admin/SettingsTab';

export default function AdminDashboard() {
  const navigate = useNavigate();
  const { currentEvent, setCurrentEvent, events: contextEvents } = useEvent();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('cars');
  
  // Shared state
  const [cars, setCars] = useState([]);
  const [classes, setClasses] = useState([]);
  const [users, setUsers] = useState([]);
  const [events, setEvents] = useState([]);
  const [categories, setCategories] = useState([]);
  const [wooConfigs, setWooConfigs] = useState([]);
  const [votingCategories, setVotingCategories] = useState([]);
  const [carsLastUpdated, setCarsLastUpdated] = useState(null);

  useEffect(() => {
    fetchUserData();
    fetchEvents();
  }, []);

  useEffect(() => {
    if (currentEvent) {
      fetchCars();
      fetchClasses();
      fetchCategories();
      fetchWooConfigs();
      fetchVotingCategories();
    }
  }, [currentEvent]);

  useEffect(() => {
    if (activeTab === 'users') {
      fetchUsers();
    }
    if (activeTab === 'events') {
      fetchWooConfigs();
    }
  }, [activeTab]);

  // Auto-refresh the cars list while admin is on the Cars tab so concurrent
  // registrations from other devices (or webhook ingestion) appear without a
  // manual refresh. Pauses while the tab is in the background to save load.
  // Period is intentionally 10s — long enough to be cheap, short enough that
  // a Reception admin sees a new walk-up registration almost immediately.
  useEffect(() => {
    if (activeTab !== 'cars' || !currentEvent) return undefined;
    const id = setInterval(() => {
      if (typeof document !== 'undefined' && document.hidden) return;
      fetchCarsQuietly();
    }, 10000);
    return () => clearInterval(id);
  }, [activeTab, currentEvent?.id]);

  const fetchUserData = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUser(response.data);
    } catch (error) {
      console.error('Error fetching user:', error);
      navigate('/login');
    } finally {
      setLoading(false);
    }
  };

  const fetchCars = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/admin/cars?event_id=${currentEvent.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCars(response.data);
      setCarsLastUpdated(new Date());
    } catch (error) {
      console.error('Error fetching cars:', error);
      toast.error('Failed to load cars');
    }
  };

  // Background auto-refresh: don't toast on failures (could just be a brief
  // network blip while the admin is mid-keystroke). The next poll will retry.
  // Uses include_photos=false so each refresh is a few KB instead of multi-MB
  // even at 1000+ car events. Photo data already in state is preserved by
  // merging the lite response with the existing cars array.
  const fetchCarsQuietly = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API}/admin/cars?event_id=${currentEvent.id}&include_photos=false`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const lite = response.data || [];
      setCars((prev) => {
        const prevById = new Map((prev || []).map((c) => [c.id, c]));
        return lite.map((c) => {
          const existing = prevById.get(c.id);
          return existing && existing.photo_data
            ? { ...c, photo_data: existing.photo_data }
            : c;
        });
      });
      setCarsLastUpdated(new Date());
    } catch (_error) {
      // silent
    }
  };

  const fetchClasses = async () => {
    try {
      const response = await axios.get(`${API}/classes?event_id=${currentEvent.id}`);
      setClasses(response.data);
    } catch (error) {
      console.error('Error fetching classes:', error);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/categories?event_id=${currentEvent.id}`);
      setCategories(response.data);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const fetchUsers = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/admin/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUsers(response.data);
    } catch (error) {
      console.error('Error fetching users:', error);
      toast.error('Failed to load users');
    }
  };

  const fetchEvents = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/events`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEvents(response.data);
    } catch (error) {
      console.error('Error fetching events:', error);
    }
  };

  const fetchWooConfigs = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/woocommerce/config`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setWooConfigs(response.data);
    } catch (error) {
      console.error('Error fetching WooCommerce configs:', error);
    }
  };

  const fetchVotingCategories = async () => {
    if (!currentEvent) return;
    try {
      const response = await axios.get(`${API}/voting-categories?event_id=${currentEvent.id}`);
      setVotingCategories(response.data);
    } catch (error) {
      console.error('Error fetching voting categories:', error);
    }
  };

  const handleEventChange = (eventId) => {
    const selectedEvent = events.find(e => e.id === eventId);
    if (selectedEvent) {
      setCurrentEvent(selectedEvent);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    navigate('/');
    toast.success('Logged out successfully');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-pink-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-20" data-testid="admin-dashboard">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-50 shadow-sm">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Logo />
          <div className="flex items-center space-x-4">
            <span className="text-gray-600">Welcome, {user?.name || user?.email}</span>
            <Button 
              onClick={handleLogout} 
              variant="outline" 
              className="border-pink-500 text-pink-600 hover:bg-pink-50"
              data-testid="logout-btn"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Page Title with Event Dropdown */}
        <div className="mb-8 flex items-center justify-between">
          <div>
            <h1 className="text-4xl font-bold text-gray-900 mb-2">Admin Dashboard</h1>
            {currentEvent && (
              <p className="text-gray-600">Managing: {currentEvent.name}</p>
            )}
          </div>
          
          {/* Event Switcher */}
          {events.length > 1 && (
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-600">Event:</span>
              <Select value={currentEvent?.id || ''} onValueChange={handleEventChange}>
                <SelectTrigger className="w-[280px] bg-white border-gray-300">
                  <SelectValue placeholder="Select event" />
                </SelectTrigger>
                <SelectContent className="bg-white border-gray-200">
                  {events.map(event => (
                    <SelectItem key={event.id} value={event.id}>
                      <span className="flex items-center gap-2">
                        <span className={`w-2 h-2 rounded-full ${
                          event.status === 'active' ? 'bg-green-500' :
                          event.status === 'upcoming' ? 'bg-blue-500' :
                          'bg-gray-400'
                        }`} />
                        {event.name}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
        </div>

        {/* Navigation Tabs */}
        <div className="flex gap-2 mb-8 border-b border-gray-200">
          <button
            onClick={() => setActiveTab('cars')}
            className={`px-6 py-3 font-medium transition-colors ${
              activeTab === 'cars'
                ? 'text-pink-600 border-b-2 border-pink-600'
                : 'text-gray-800 hover:text-gray-900'
            }`}
            data-testid="cars-tab"
          >
            <Car className="w-4 h-4 inline mr-2" />
            Cars Management
          </button>
          <button
            onClick={() => setActiveTab('users')}
            className={`px-6 py-3 font-medium transition-colors ${
              activeTab === 'users'
                ? 'text-pink-600 border-b-2 border-pink-600'
                : 'text-gray-800 hover:text-gray-900'
            }`}
            data-testid="users-tab"
          >
            <Users className="w-4 h-4 inline mr-2" />
            Users & Judges
          </button>
          <button
            onClick={() => setActiveTab('events')}
            className={`px-6 py-3 font-medium transition-colors ${
              activeTab === 'events'
                ? 'text-pink-600 border-b-2 border-pink-600'
                : 'text-gray-800 hover:text-gray-900'
            }`}
            data-testid="events-tab"
          >
            <Calendar className="w-4 h-4 inline mr-2" />
            Event Info
          </button>
          <button
            onClick={() => setActiveTab('settings')}
            className={`px-6 py-3 font-medium transition-colors ${
              activeTab === 'settings'
                ? 'text-pink-600 border-b-2 border-pink-600'
                : 'text-gray-800 hover:text-gray-900'
            }`}
            data-testid="settings-tab"
          >
            <Settings className="w-4 h-4 inline mr-2" />
            Settings
          </button>
        </div>

        {/* Tab Content */}
        {activeTab === 'cars' && (
          <CarsTab 
            currentEvent={currentEvent}
            cars={cars}
            setCars={setCars}
            classes={classes}
            fetchCars={fetchCars}
            carsLastUpdated={carsLastUpdated}
          />
        )}

        {activeTab === 'users' && (
          <UsersTab 
            currentUser={user}
            users={users}
            events={events}
            fetchUsers={fetchUsers}
          />
        )}

        {activeTab === 'events' && (
          <EventsTab 
            currentUser={user}
            events={events}
            classes={classes}
            categories={categories}
            votingCategories={votingCategories}
            fetchEvents={fetchEvents}
            fetchClasses={fetchClasses}
            fetchCategories={fetchCategories}
            fetchVotingCategories={fetchVotingCategories}
          />
        )}

        {activeTab === 'settings' && (
          <SettingsTab 
            currentUser={user}
            wooConfigs={wooConfigs}
            events={events}
            fetchWooConfigs={fetchWooConfigs}
          />
        )}
      </div>
    </div>
  );
}
